import { useMemo } from "react";
import { useRoles } from "newui/features/RolesPermissions/hooks/useUserAssignments";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";

interface RbacRolePillsProps {
	assignment: UserAssignment | null;
}

/**
 * Renders a user's RBAC v2 roles as pills (the union of their assignments).
 *
 * AC5: multi-role users show the union of their roles; effective per-module level
 * is server-authoritative (EffectivePermissionsResource). This never client-computes
 * a most-restrictive intersection — it lists the assigned roles. Shared between the
 * account users table and any RBAC-aware row.
 *
 * PRO-732c: the "hybrid" badge is NOT "more than one role" — it only means the
 * user holds roles on BOTH sides (procurer and bidder). A bidder-only company's
 * user with two bidder-side roles is not hybrid. `UserRoleAssignment` carries no
 * `side` of its own, so this joins each assignment's `role_id` to the cached
 * roles list (useRoles, react-query) to resolve it. Calling the hook here (rather
 * than threading a side lookup down as a prop) keeps both call sites (UserListRow,
 * UserDrawer) unchanged.
 */
export const RbacRolePills = ({ assignment }: RbacRolePillsProps) => {
	const { roles } = useRoles();
	const roleSideById = useMemo(() => {
		const map = new Map<number, string | null | undefined>();
		roles.forEach((role) => map.set(role.id, role.side));
		return map;
	}, [roles]);

	const assignedRoles = assignment?.assignments ?? [];

	if (assignedRoles.length === 0) {
		return <span className="text-xs text-gray-400">No roles</span>;
	}

	// Roles still loading or a cloned/unknown role_id resolve to `undefined` here
	// and are simply not counted towards either side — degrade to no badge rather
	// than guessing.
	const hasProcurerRole = assignedRoles.some(
		(role) => roleSideById.get(role.role_id) === "procurer"
	);
	const hasBidderRole = assignedRoles.some(
		(role) => roleSideById.get(role.role_id) === "bidder"
	);
	const isHybrid = hasProcurerRole && hasBidderRole;

	return (
		<div className="flex flex-wrap items-center gap-1.5">
			{assignedRoles.map((role) => (
				<span
					key={role.assignment_id}
					className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700"
				>
					{role.role_name}
					{role.scope_label ? ` · ${role.scope_label}` : ""}
				</span>
			))}
			{isHybrid && (
				<span
					className="rounded bg-blue-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white"
					title="Hybrid — holds both procurer and bidder roles; effective access is the union (most-permissive)"
					data-testid={`hybrid-badge-${assignment?.id}`}
				>
					hybrid
				</span>
			)}
		</div>
	);
};
